import currencies from '@/assets/currencies.json'

/**
 * Read the current company group's settings from localStorage. Returns {} when
 * the value is missing or malformed so callers can safely destructure.
 */
export function getGroupSettings() {
  try {
    return JSON.parse(localStorage.getItem('group')) || {}
  } catch {
    return {}
  }
}

/**
 * Resolve the currency descriptor for a currency code (or the active group's
 * currency when no code is passed). Returns `{ symbol, postfix }`, falling
 * back to the resolved code itself (e.g. "SEK") when it isn't in
 * currencies.json — that's safer than silently rendering '$' on a non-USD
 * currency.
 */
export function getCurrencyInfo(code) {
  const resolved = code || getGroupSettings().currency || 'USD'
  const info = currencies.currencies.find(c => c.code === resolved)
  return {
    symbol: info ? info.symbol : resolved,
    postfix: info ? !!info.postfix : false
  }
}

/**
 * Format a numeric value as a currency string per the company group's settings.
 *
 * Mirrors the canonical getAmount() pattern that was duplicated across the
 * BookingDrawer parent and its child views (CollectPaymentView,
 * CreateBookingView, EditTicketsView, RescheduleView). Use this anywhere a
 * monetary amount needs to be displayed — never hardcode `$` + `.toFixed(2)`.
 *
 * @param {number|string} value
 * @param {Object} [options]
 * @param {string} [options.currency='USD']      ISO currency code from group.currency
 * @param {string} [options.currencySymbol='$']  Fallback symbol when currency code isn't in currencies.json
 * @param {number} [options.currencyFormat=0]    0 = symbol prefix/postfix, 1 = symbol + space (postfix-aware)
 * @param {number} [options.numberFormat=0]      0 = US (1,234.56), 1 = EU (1.234,56)
 * @returns {string}
 */
export function formatCurrency(value, options = {}) {
  if (value == null) value = 0
  const {
    currency = 'USD',
    currencySymbol = '$',
    currencyFormat = 0,
    numberFormat = 0
  } = options

  const currencyInfo = currencies.currencies.find(c => c.code === currency)
  const symbol = currencyInfo ? currencyInfo.symbol : currencySymbol
  const isPostfix = currencyInfo ? currencyInfo.postfix : false

  const number = parseFloat(value)
  // Half-cent threshold so float residue on a settled balance (for example
  // -1.4e-14) never prints a minus sign in front of 0.00.
  const sign = number < -0.005 ? '-' : ''
  const magnitude = number < -0.005 ? -number : Math.max(number, 0)

  const formatted = numberFormat === 1
    ? magnitude.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',')
    : magnitude.toFixed(2)

  if (currencyFormat === 0) {
    return sign + (isPostfix ? formatted + symbol : symbol + formatted)
  }
  return sign + (isPostfix ? formatted + ' ' + symbol : symbol + ' ' + formatted)
}
